import React, { Suspense } from "react"
import classNames from "classnames"

import { iconNames, iconForName } from "./Icon.config"
import "./Icon.css"

import { generateMarginClasses } from "@utils/spacing"

const baseClass = "wb-icon"

/**
 * Display a UI icon in different sizes. All icons are from the [Core Line –
 * Free](https://www.streamlinehq.com/icons/streamline-mini-line) set, exported
 * mostly with an increased stroke width of 1.5 and size of 16px and minor size
 * adjustments to fit into a 20x20px frame with some negative space depending on
 * how contrasty/striking the image is.
 */

const iconSizes = ["extra-small", "small", "medium", "large"]

type IconProps = {
  /**
   * The icon name.
   */
  name: typeof iconNames[number],
  /**
   * Select a size from ["extra-small", "small", "medium", "large"]. Defaults to "medium".
   */
  size?: typeof iconSizes[number],
  /**
   * Attach a className to manipulate the styling (only if necessary).
   */
  className?: string,
  /**
   * Select a color for the icon.
   */
  color?: 'forest-green' | 'light-gray' | 'golden-rod' | 'salmon-pink' | 'secondary-main-legacy' | 'error' | 'primary-main' | 'tertiary-main' | 'secondary-main' | 'grey',
  /** Margin is a shorthand prop for applying margin to the text. */
  margin?: string | object, // Deprecated
}

const Icon = ({
  name,
  size = "medium",
  color,
  margin,
  ...rest
}: IconProps) => {
  const { className, ...passThroughProps } = rest
  // Deprecated
  const marginClasses = generateMarginClasses(margin)

  const classes = classNames(
    'wb-icon',
    `wb-icon--${size}`,
    color && `wb-icon--${color}`,
    className,
    marginClasses
  )

  return (
    <span className={classes} aria-hidden="true" {...passThroughProps}>
      <Suspense fallback={null}>
        {iconForName(name)}
      </Suspense>
    </span>
  )
}

export { Icon, IconProps }
